import {mergeMap, switchMap, delay} from "rxjs/operators";
import {ofType} from "redux-observable";
import {translate} from "react-i18nify";

import * as actionTypes from "../constants/actionTypes";
import * as componentTypes from "../constants/componentTypes";
import * as config from "../constants/config";
import * as handlers from "../constants/handlers";
import * as methods from "../constants/methods";
import * as modules from "../constants/modules";
import * as resources from "../constants/resources";
import * as resultCodes from "../constants/resultCodes";
import * as navigationActions from "../actions/navigationActions";
import * as responseService from "../services/responseService";
import * as eventResponseService from "../services/eventResponseService";
import * as validationHelper from "../helpers/validationHelper";
import * as windowService from "../services/windowService";
import * as changelogService from "../services/changelogService";
import * as filteringHelper from "../helpers/filteringHelper";
import * as messageHelper from "../helpers/messageHelper";
import * as modelHelper from "../helpers/modelHelper";
import * as translationHelper from "../helpers/translationHelper";

import Server from "../models/utils/Server";

const connectionOpenedAction = (action$) =>
    action$.pipe(
        ofType(actionTypes.WEBSOCKET_OPENED),
        switchMap(() => {
            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_LICENSE_STATUS
                    },
                    method: handlers.GET_LICENSE_STATUS,
                    tabId: 0
                }
            ];
        })
    );

const setupCompleted = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SETUP_COMPLETED),
        switchMap(() => {
            if (state$.value.module.setupSoftware) {
                return [];
            }

            return [
                {
                    type: actionTypes.SOFTWARE_NOT_INITIALIZED
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_LICENSE_STATUS
                    },
                    method: handlers.GET_LICENSE_STATUS,
                    tabId: 0
                }
            ];
        })
    );

const serverStartInitialization = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SOFTWARE_START_INITIALIZATION),
        switchMap(() => {
            let software, name;
            if (state$.value.module.name === modules.MODULE_FRONTDESK) {
                software = config.SOFTWARE_CLIENT_WITHOUT_RCC;
                name = config.IG_FRONTDESK;
            } else if (state$.value.module.name === modules.MODULE_MACHINE) {
                software = config.SOFTWARE_CLIENT;
                name = config.IG_MACHINE;
            } else {
                software = config.SOFTWARE_CLIENT;
                name = config.REDLIKE_CLIENT;
            }

            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_INIT,
                        params: {
                            software: software,
                            name: name,
                            version: changelogService.getLastClientVersion(),
                            database: windowService.getDefaultDatabase(),
                            clientIdentifier: windowService.getClientIdentifier()
                        }
                    },
                    method: handlers.INIT,
                    tabId: 0
                }
            ];
        })
    );

const websocketResponse = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.WEBSOCKET_RESPONSE),
        mergeMap(({payload}) => {
            window.storeProvider.resetLastServerActivity();

            if (payload.data === methods.METHOD_PONG) {
                return [];
            }

            let response = null;

            try {
                response = JSON.parse(payload.data);
            } catch (e) {
                response = null;
            }

            if (response === null) {
                windowService.logResponse("Server", payload.data);
                return [{type: actionTypes.UNKNOWN_ACTION}];
            }

            if (response.sequence === undefined || isNaN(response.sequence)) {
                windowService.logResponse("Server", payload.data);

                if (response.event !== undefined) {
                    return eventResponseService.processWebsocketEventResponse(response, state$.value);
                }

                return [{type: actionTypes.UNKNOWN_ACTION}];
            }

            if (response.result === undefined) {
                windowService.logResponse("Server", payload.data);
                return [{type: actionTypes.UNKNOWN_ACTION}];
            }

            if (response.result === resultCodes.RESULT_CLIENT_NOT_LOGGED_IN) {
                windowService.logResponse("Server", payload.data);
                return [{type: actionTypes.LOG_OUT_SUCCESS}];
            }

            let sequence = Number(response.sequence);

            let defaultRequests = [
                {
                    type: actionTypes.REMOVE_WEBSOCKET_REQUEST,
                    payload: {
                        sequence: sequence
                    }
                }
            ];

            let request = state$.value.server.requests.find((request) => request.id === sequence);

            if (request === undefined || request.skip) {
                windowService.logResponse("Server", payload.data);
                return defaultRequests;
            }

            windowService.logResponse("Server", payload.data, request.time);

            return defaultRequests.concat(
                responseService.processWebsocketResponse(request.tabId, request.method, response, state$.value)
            );
        })
    );

const connectDefaultServer = (action$) =>
    action$.pipe(
        ofType(actionTypes.CONNECT_DEFAULT_SERVER),
        switchMap(() => {
            return [
                {
                    type: actionTypes.SET_ACTIVE_SERVER,
                    payload: {
                        server: new Server(windowService.getDefaultServer(), config.SERVER_PORT, null, "Default")
                    }
                }
            ];
        })
    );

const setActiveServer = (action$) =>
    action$.pipe(
        ofType(actionTypes.SET_ACTIVE_SERVER),
        switchMap(({payload}) => {
            windowService.setDefaultServer(payload.server.ip);
            return [
                {
                    type: actionTypes.SERVER_CONNECT,
                    payload: {
                        url: payload.server.getWebSocketUrl()
                    }
                }
            ];
        })
    );

const deleteImportedRecords = (action$) =>
    action$.pipe(
        ofType(actionTypes.DELETE_ALL_IMPORTED_RECORDS),
        switchMap(({}) => {
            return [
                {
                    type: actionTypes.CUSTOM_DATA_DELETE_IMPORTED_DATA
                },
                messageHelper.getInfoMessageAction("flashMessage.deletingImportedData")
            ];
        })
    );

const saveActiveServer = (action$) =>
    action$.pipe(
        ofType(actionTypes.SET_ACTIVE_SERVER_BY_URL),
        switchMap(({payload}) => {
            if (!validationHelper.isWebSocketUrlValid(payload.url)) {
                return [messageHelper.getWarningMessageAction("flashMessage.invalidUrl")];
            }

            windowService.setDefaultServerByUrl(payload.url);

            return [
                {
                    type: actionTypes.SAVE_ACTIVE_SERVER_URL,
                    payload: {
                        url: payload.url
                    }
                },
                {
                    type: actionTypes.SERVER_CONNECT,
                    payload: {
                        url: payload.url
                    }
                }
            ];
        })
    );

const resetServerConnection = (action$) =>
    action$.pipe(
        ofType(actionTypes.RESET_SERVER_CONNECTION),
        switchMap(() => {
            return [
                {
                    type: actionTypes.CLOSE_SERVER_CONNECTION,
                    payload: {
                        sequence: config.SERVER_INIT_SEQUENCE
                    }
                }
            ];
        })
    );

const closeServerConnection = (action$) =>
    action$.pipe(
        ofType(actionTypes.CLOSE_SERVER_CONNECTION),
        switchMap(() => {
            return [
                {
                    type: actionTypes.SERVER_DISCONNECT
                }
            ];
        })
    );

const getDatabaseImages = (action$) =>
    action$.pipe(
        ofType(actionTypes.SAVE_DATABASE_LIST),
        switchMap(({payload}) => {
            let database,
                requests = [];
            for (database of payload.databaseList) {
                if (window.imageStore.dbImageIsEmpty(database.name)) {
                    requests.push({
                        type: actionTypes.SERVER_REQUEST,
                        payload: {
                            type: methods.METHOD_GET_DATABASE_IMAGE,
                            params: {
                                name: database.name
                            }
                        },
                        method: handlers.GET_DATABASE_IMAGE,
                        tabId: componentTypes.NO_SKIP_TAB_ID
                    });
                }
            }

            return requests;
        })
    );

const databaseChange = (action$) =>
    action$.pipe(
        ofType(actionTypes.DATABASE_CHANGE),
        delay(1000),
        switchMap(() => {
            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_ACTIVE_DATABASE
                    },
                    method: handlers.GET_ACTIVE_DATABASE_AND_LOG_IN,
                    tabId: 0
                }
            ];
        })
    );

const dropServers = (action$) =>
    action$.pipe(
        ofType(actionTypes.DROP_SERVERS),
        switchMap(() => {
            return [
                {
                    type: actionTypes.SAVE_SERVER_MANAGEMENT_LIST,
                    payload: {
                        serverManagementList: []
                    }
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_DELETE_REDLIKE_SERVERS
                    },
                    method: handlers.DELETE_REDLIKE_SERVERS,
                    tabId: 0
                }
            ];
        })
    );

const preparePricingDataFile = (action$) =>
    action$.pipe(
        ofType(actionTypes.PREPARE_FILE_PRICING_DATA),
        switchMap(() => {
            return [
                {
                    type: actionTypes.SERVER_SAVE_FILE_INFORMATION,
                    payload: {
                        downloadingFile: modelHelper.mapDownloadingFileToObject(-1, "PRICES", "xlsx", 1)
                    }
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_PREPARE_FILE,
                        params: {
                            method: methods.METHOD_EXPORT_PRICING_DATA
                        }
                    },
                    method: handlers.PREPARE_FILE_PRICING_DATA,
                    tabId: 0
                },
                {
                    type: actionTypes.NAVIGATION_OPEN_MODAL,
                    payload: {
                        tabId: componentTypes.MODAL_NO_TAB,
                        type: componentTypes.PROGRESS_BAR_MODAL
                    }
                }
            ];
        })
    );

const prepareStatsDataFile = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.PREPARE_FILE_STATS_DATA),
        switchMap(({payload}) => {
            if (state$.value.filtering[payload.tabId] === undefined) {
                return [];
            }

            let requestParams = null;
            if (payload.all) {
                requestParams = {
                    orderBy: state$.value.statisticsTab.sortingCriterion,
                    asc: state$.value.statisticsTab.sortingAsc,
                    offset: 0,
                    rowCount: config.NO_LIMIT,
                    visibleOnly: !state$.value.statisticsTab.showHidden,
                    columns: translationHelper.getDispensingHistoryExportColumns(
                        state$.value.settings,
                        state$.value.login.loggedUser.canRead(resources.COMPONENT_BUILDING_SITE_PANEL)
                    )
                };
            } else {
                const statsParams = filteringHelper.getFilteringParams(payload.tabId, state$.value);

                requestParams = {
                    dateFrom: statsParams.dateFrom,
                    dateTo: statsParams.dateTo,
                    products: statsParams.productList,
                    fandecks: statsParams.fandeckList,
                    colors: statsParams.formulaList,
                    bases: statsParams.baseList,
                    packages: statsParams.packageList,
                    customers: statsParams.customerList,
                    search: state$.value.statisticsTab.filter,
                    searchByColumns: statsParams.searchByColumns,
                    orderBy: state$.value.statisticsTab.sortingCriterion,
                    asc: state$.value.statisticsTab.sortingAsc,
                    offset: 0,
                    rowCount: config.NO_LIMIT,
                    visibleOnly: !state$.value.statisticsTab.showHidden,
                    columns: translationHelper.getDispensingHistoryExportColumns(
                        state$.value.settings,
                        state$.value.login.loggedUser.canRead(resources.COMPONENT_BUILDING_SITE_PANEL)
                    )
                };
            }

            if (payload.path) {
                return [
                    {
                        type: actionTypes.SERVER_REQUEST,
                        payload: {
                            type: methods.METHOD_EXPORT_STATS,
                            params: {
                                path: payload.path,
                                ...requestParams
                            }
                        },
                        method: handlers.EXPORT_STATISTICS_HISTORY,
                        tabId: payload.tabId
                    },
                    navigationActions.openLoadingModal(
                        payload.tabId,
                        translate("navigation.menu.dispensingHistory") + " - " + translate("pricing.export"),
                        null
                    )
                ];
            }

            return [
                {
                    type: actionTypes.SERVER_SAVE_FILE_INFORMATION,
                    payload: {
                        downloadingFile: modelHelper.mapDownloadingFileToObject(-2, "HISTORY", "xlsx", 1)
                    }
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_PREPARE_FILE,
                        params: {
                            method: methods.METHOD_EXPORT_STATS,
                            params: requestParams
                        }
                    },
                    method: handlers.PREPARE_FILE_STATS_DATA,
                    tabId: payload.tabId
                },
                {
                    type: actionTypes.NAVIGATION_OPEN_MODAL,
                    payload: {
                        tabId: payload.tabId,
                        type: componentTypes.PROGRESS_BAR_MODAL
                    }
                }
            ];
        })
    );

const prepareFileSuccess = (action$) =>
    action$.pipe(
        ofType(actionTypes.SERVER_PREPARE_FILE_SUCCESS),
        switchMap(({payload}) => {
            let remainingLength = payload.preparedFile.totalLength - payload.preparedFile.downloadedLength;

            if (remainingLength > config.SERVER_CHUNK_SIZE) {
                return [
                    {
                        type: actionTypes.SERVER_REQUEST,
                        payload: {
                            type: methods.METHOD_PREPARE_FILE_BYTES,
                            params: {
                                id: payload.preparedFile.id,
                                bytesFrom: payload.preparedFile.downloadedLength,
                                bytesTo: payload.preparedFile.downloadedLength + config.SERVER_CHUNK_SIZE
                            }
                        },
                        method: handlers.PREPARE_FILE_BYTES,
                        tabId: 0
                    },
                    {
                        type: actionTypes.SERVER_UPDATE_FILE_INFORMATION,
                        payload: {
                            downloadingFile: payload.preparedFile.updateDownloadedLength(
                                payload.preparedFile.downloadedLength + config.SERVER_CHUNK_SIZE
                            )
                        }
                    }
                ];
            }

            if (remainingLength < config.SERVER_CHUNK_SIZE && remainingLength > 0) {
                return [
                    {
                        type: actionTypes.SERVER_REQUEST,
                        payload: {
                            type: methods.METHOD_PREPARE_FILE_BYTES,
                            params: {
                                id: payload.preparedFile.id,
                                bytesFrom: payload.preparedFile.downloadedLength,
                                bytesTo: payload.preparedFile.totalLength
                            }
                        },
                        method: handlers.PREPARE_FILE_BYTES,
                        tabId: 0
                    },
                    {
                        type: actionTypes.SERVER_UPDATE_FILE_INFORMATION,
                        payload: {
                            downloadingFile: payload.preparedFile.updateDownloadedLength(
                                payload.preparedFile.downloadedLength + remainingLength
                            )
                        }
                    }
                ];
            }

            windowService.openMoveFileDialog(payload.preparedFile.filePath, payload.preparedFile.fileName);

            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_RELEASE_PREPARED_FILE,
                        params: {
                            id: payload.preparedFile.id
                        }
                    },
                    method: handlers.RELEASE_PREPARED_FILE,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_DELETE_DOWNLOADED_FILE,
                    payload: {
                        downloadedFileId: payload.preparedFile.id
                    }
                },
                messageHelper.getSuccessMessageAction("flashMessage.fileExportComplete")
            ];
        })
    );

const prepareFileError = (action$) =>
    action$.pipe(
        ofType(actionTypes.SERVER_PREPARE_FILE_ERROR),
        switchMap(({payload}) => {
            return [
                {
                    type: actionTypes.SET_BLOCKED_UI,
                    payload: {
                        blocked: false
                    }
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_RELEASE_PREPARED_FILE,
                        params: {
                            id: payload.preparedFileId
                        }
                    },
                    method: handlers.RELEASE_PREPARED_FILE,
                    tabId: 0
                },
                messageHelper.getErrorMessageAction("flashMessage.problemWithFileExport")
            ];
        })
    );

const startDatabaseUpdate = (action$) =>
    action$.pipe(
        ofType(actionTypes.START_DATABASE_UPDATE),
        switchMap(() => {
            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_UPDATE_DATABASES
                    },
                    method: handlers.UPDATE_DATABASES,
                    tabId: 0
                }
            ];
        })
    );

export default [
    connectionOpenedAction,
    setupCompleted,
    serverStartInitialization,
    websocketResponse,
    connectDefaultServer,
    setActiveServer,
    saveActiveServer,
    resetServerConnection,
    closeServerConnection,
    getDatabaseImages,
    databaseChange,
    dropServers,
    preparePricingDataFile,
    prepareStatsDataFile,
    prepareFileSuccess,
    prepareFileError,
    startDatabaseUpdate,
    deleteImportedRecords
];
